




import Vue, { PropType } from "vue";
import { Chart } from "chart.js";
import {
  Data,
  Methods,
  Computed,
  Props,
  CHART_TYPE,
  TARGET_KEY
} from "./types";
// @ts-ignore
import { customDraw } from "./roundedBar";

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "LikedChart",
  props: {
    type: {
      type: String as PropType<Props["type"]>,
      required: true
    },
    report: {
      type: Object as PropType<Props["report"]>,
      required: true
    }
  },
  data() {
    return {
      bindTo: `liked-chart-${this.type}`,
      chart: null,
      chartData: {
        labels: [], // NOTE generateLabelsで動的に生成する
        datasets: [
          {
            type: "line",
            label: `週のお気に入り数`,
            yAxisID: TARGET_KEY.INCREMENTS,
            borderWidth: 1.8,
            borderColor: "#FF6388",
            backgroundColor: "#FF6388",
            lineTension: 0,
            fill: false,
            data: []
          },
          {
            type: "bar",
            label: `累計のお気に入り数`,
            yAxisID: TARGET_KEY.TOTAL,
            barPercentage: 0.1,
            borderWidth: 0.2,
            borderColor: "#36CBE5",
            backgroundColor: "#DBF2F6",
            data: []
          }
        ]
      },
      chartOptions: {
        maintainAspectRatio: false,
        cornerRadius: 5,
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              offset: true,
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              id: TARGET_KEY.INCREMENTS,
              ticks: {
                min: 0,
                padding: 25
              },
              gridLines: {
                borderDash: [3, 3],
                drawBorder: false,
                tickMarkLength: 0
              }
            },
            {
              id: TARGET_KEY.TOTAL,
              position: "right",
              ticks: {
                min: 0,
                padding: 18
              },
              gridLines: {
                borderDash: [3, 3],
                drawBorder: false,
                tickMarkLength: 0
              }
            }
          ]
        }
      }
    };
  },
  watch: {
    report: {
      immediate: true,
      deep: true,
      handler(val) {
        this.onChangeReport(val);
      }
    },
    "chartData.datasets": {
      immediate: true,
      handler() {
        this.onChangeChartData();
      }
    },
    "chartData.labels"() {
      this.onChangeChartData();
    }
  },
  created() {
    this.customDrawInit();
  },
  mounted() {
    this.generateChart();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    generateChart() {
      if (this.chart) this.chart.destroy();
      const canvas = document.getElementById(this.bindTo) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if (!canvas || !ctx) return;

      this.chart = new Chart(ctx, {
        data: this.chartData,
        options: this.chartOptions
      });
    },
    generateData(reports) {
      const incrementsData: number[] = [];
      const totalData: number[] = [];
      reports.forEach(report => {
        incrementsData.push(report.liked_report.increments);
        totalData.push(report.liked_report.total);
      });
      return { incrementsData, totalData };
    },
    // NOTE - generateLabels データが欠けている(グラフが左寄せになる)場合も共通で処理可能
    // MONTHLYの場合、2020年1月~11月を選択するとデータが欠ける(report.reports.length < 12)
    // WEEKLYの場合、2020年1月1週~3週を選択するとデータが欠ける(report.reports.length < 4)
    // 欠けている期間のラベルは空文字となる
    // ※途中の期間が欠けることはない(累計は常に集計するため)
    generateLabels(reports) {
      const labelsLength = this.type === CHART_TYPE.WEEKLY ? 4 : 12;
      let labels: string[] = Array(labelsLength).fill("");

      // NOTE 週の場合のラベル
      if (this.type === CHART_TYPE.WEEKLY) {
        let monthFromBeforeData = 0;
        reports.map((report, i) => {
          const { month, week } = report;
          // NOTE 前データの月と処理対象の月が違う場合ラベルに月を表示
          if (monthFromBeforeData !== month) {
            labels[i] = `${month}月${week}週目`;
          } else {
            labels[i] = `${week}週目`;
          }
          monthFromBeforeData = month;
        });
        // NOTE 年の場合のラベル
      } else {
        let yearFromBeforeData = 0;
        reports.map((report, i) => {
          const { year, month } = report;
          // NOTE 前データの年と処理対象の年が違う場合ラベルに年を表示
          if (yearFromBeforeData !== year) {
            labels[i] = `${year}年${month}月`;
          } else {
            labels[i] = `${month}月`;
          }
          yearFromBeforeData = year;
        });
      }
      return labels;
    },
    customDrawInit() {
      // @ts-ignore
      Chart.elements.Rectangle.prototype.draw = customDraw;
    },
    onChangeChartData() {
      if (!this.chart) return;
      this.chart.update();
      this.$emit("chart:update");
    },
    onChangeReport(report) {
      // ラベル生成
      const labels = this.generateLabels(report.reports);
      this.chartData.labels = labels;

      // データ生成
      const { incrementsData, totalData } = this.generateData(report.reports);

      this.chartData.datasets![0].data = incrementsData;
      this.chartData.datasets![1].data = totalData;

      // 目盛り(Ticks)生成
      const [
        maxValueForIncrementsAxes,
        stepSizeForIncrementsAxes
      ] = this.findMaxValueForTicks(incrementsData);
      const [
        maxValueForTotalAxes,
        stepSizeForTotalAxes
      ] = this.findMaxValueForTicks(totalData);

      // NOTE dataに定義しており、undefinedはあり得ないため "!" を使用している
      this.chartOptions.scales!.yAxes![0].ticks!.max = maxValueForIncrementsAxes;
      this.chartOptions.scales!.yAxes![0].ticks!.stepSize = stepSizeForIncrementsAxes;
      this.chartOptions.scales!.yAxes![1].ticks!.max = maxValueForTotalAxes;
      this.chartOptions.scales!.yAxes![1].ticks!.stepSize = stepSizeForTotalAxes;

      this.onChangeChartData();
    },
    findMaxValueForTicks(data) {
      const maxValueFromData = Math.max(...data);
      // NOTE 桁数
      const digit = String(Math.floor(maxValueFromData)).length;
      // NOTE 1桁の場合は4の倍数、2桁の場合は20の倍数、3桁以上の場合は100の倍数
      const divisionValue = digit === 1 ? 4 : digit === 2 ? 20 : 100;
      // NOTE incrementsがすべて0のケースがあり得るため、その場合は1を初期値をとする(0の場合、後続の商を求める際に常に余りが0となり想定した結果とならないため)
      let maxValue = maxValueFromData | 1;
      // NOTE 割り切れるまで再帰処理で +1していく
      while (maxValue % divisionValue !== 0) {
        maxValue++;
      }
      const stepSize = maxValue / 4;
      return [maxValue, stepSize];
    }
  }
});
